import { t } from "i18next";
import moment from "moment";
import { utils, write } from "xlsx";
import {
  Populater,
  type ProductFieldDto,
  type ProductGroupDto,
  type ReceivedBidDto,
  type SourcingEventDto,
  type WinningProductGroupBidsDto,
} from "../../../autogen/bff-api";
import { generateUniqueExcelSheetName, sanitizeFilename } from "../../../common/utils/sanitizeUtils";

type Cell = string | number | boolean | undefined | null;

export interface ProductReportSheet {
  name: string;
  rows: Cell[][];
}

export const buildProductEvaluationSheets = (
  groups: ProductGroupDto[],
  bids: ReceivedBidDto[],
  selection: WinningProductGroupBidsDto[],
): ProductReportSheet[] => {
  const usedSheetNames = new Set<string>();

  return groups.map((group) => {
    const buyerFields = group.productFields.filter((field) => field.populatedBy === Populater.Buyer);
    const supplierFields = group.productFields.filter((field) => field.populatedBy === Populater.Supplier);
    const headings: Cell[] = [
      ...buyerFields.map((field) => field.name),
      t("Quantity"),
      t("Unit Price"),
      t("Total price"),
      t("Awarded to"),
      ...supplierFields.map((field) => field.name),
    ];

    const groupSelection = selection.find((s) => s.productGroupId === group.id);
    const rows = group.products.flatMap((product) => {
      const selectedBidIds = groupSelection?.bids.find((b) => b.productId === product.id)?.bids ?? [];
      return bids
        .filter((bid) => selectedBidIds.includes(bid.id))
        .map((bid) => {
          const productBid = productBidFor(bid, group.id, product.id);
          const quantity = product.quantity.value;
          const unitPrice = productBid?.unitPrice.value;
          return [
            ...buyerFields.map((field) => fieldValue(product.productFields, field.name)),
            quantity,
            unitPrice,
            unitPrice != null ? (quantity ?? 1) * unitPrice : undefined,
            bid.ownerName,
            ...supplierFields.map((field) => fieldValue(productBid?.productFields ?? [], field.name)),
          ];
        });
    });

    return { name: generateUniqueExcelSheetName(group.name, usedSheetNames), rows: [headings, ...rows] };
  });
};

export const buildProductPriceComparisonSheets = (
  groups: ProductGroupDto[],
  bids: ReceivedBidDto[],
  event: SourcingEventDto,
  downloadedAt: string,
): ProductReportSheet[] => {
  const usedSheetNames = new Set<string>();

  return groups.map((group) => {
    const buyerFields = group.productFields.filter((field) => field.populatedBy === Populater.Buyer);
    const metaRows: Cell[][] = [[t("Procurement"), event.eventData.title], [t("Downloaded at"), downloadedAt], []];
    const headings: Cell[] = [
      ...buyerFields.map((field) => field.name),
      t("Quantity"),
      ...bids.map((bid) => bid.ownerName),
    ];
    const productRows: Cell[][] = group.products.map((product) => [
      ...buyerFields.map((field) => fieldValue(product.productFields, field.name)),
      product.quantity.value ?? undefined,
      ...bids.map((bid) => productBidFor(bid, group.id, product.id)?.unitPrice.value ?? undefined),
    ]);

    return { name: generateUniqueExcelSheetName(group.name, usedSheetNames), rows: [...metaRows, headings, ...productRows] };
  });
};

export const generateProductEvaluationReport = (
  groups: ProductGroupDto[],
  bids: ReceivedBidDto[],
  event: SourcingEventDto,
  selection: WinningProductGroupBidsDto[],
) => {
  if (!groups.length || !bids.length) throw Error("Could not generate report. No product groups found.");
  downloadWorkbook(
    buildProductEvaluationSheets(groups, bids, selection),
    `${sanitizeFilename(event.eventData.title)}.xlsx`,
  );
};

export const generateProductPriceComparisonReport = (
  groups: ProductGroupDto[],
  bids: ReceivedBidDto[],
  event: SourcingEventDto,
) => {
  if (!groups.length || !bids.length)
    throw Error("Could not generate price comparison. No product groups or bids found.");
  const downloadedAt = moment().format("DD.MM.YYYY HH:mm");
  downloadWorkbook(
    buildProductPriceComparisonSheets(groups, bids, event, downloadedAt),
    `${sanitizeFilename(event.eventData.title)}_${sanitizeFilename(t("Unit Price"))}.xlsx`,
  );
};

const productBidFor = (bid: ReceivedBidDto, groupId: string, productId: string) =>
  bid.products?.find((g) => g.productGroupId === groupId)?.productBids.find((p) => p.productId === productId);

const fieldValue = (fields: ProductFieldDto[], name: string): Cell =>
  fields.find((field) => field.name === name)?.value ?? undefined;

const downloadWorkbook = (sheets: ProductReportSheet[], fileName: string) => {
  const workbook = utils.book_new();
  for (const sheet of sheets) {
    utils.book_append_sheet(workbook, utils.aoa_to_sheet(sheet.rows), sheet.name);
  }

  const arrayBuffer = write(workbook, { bookType: "xlsx", type: "buffer" });
  const blob = new Blob([arrayBuffer], { type: "application/octet-stream" });
  const url = window.URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.click();

  window.URL.revokeObjectURL(url);
};
